class WishlistManager {
  constructor() {
    this.storageKey = "shopify_wishlist";
  }
  getWishlist() {
    try {
      const wishlist = localStorage.getItem(this.storageKey);
      return wishlist ? JSON.parse(wishlist) : [];
    } catch (error) {
      console.error("Error reading wishlist:", error);
      return [];
    }
  }
  addToWishlist(productId) {
    const wishlist = this.getWishlist();
    if (!wishlist.includes(productId)) {
      wishlist.push(productId);
      this.saveWishlist(wishlist);
      this.dispatchWishlistUpdate();
      return true;
    }
    return false;
  }
  removeFromWishlist(productId) {
    let wishlist = this.getWishlist();
    const index = wishlist.indexOf(productId);
    if (index > -1) {
      wishlist.splice(index, 1);
      this.saveWishlist(wishlist);
      this.dispatchWishlistUpdate();
      return true;
    }
    return false;
  }
  isInWishlist(productId) {
    return this.getWishlist().includes(productId);
  }
  saveWishlist(wishlist) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(wishlist));
    } catch (error) {
      console.error("Error saving wishlist:", error);
    }
  }
  dispatchWishlistUpdate() {
    window.dispatchEvent(
      new CustomEvent("wishlist:updated", {
        detail: { wishlist: this.getWishlist() }
      })
    );
  }
}
if (!window.wishlistManager) {
  window.wishlistManager = new WishlistManager();
}
class WishlistRender extends window.HTMLElement {
  constructor() {
    super();
    this.productsContainer = null;
    this.emptyState = null;
    this.loadingState = null;
  }
  connectedCallback() {
    this.productsContainer = this.querySelector("[data-wishlist-products]");
    this.emptyState = this.querySelector("[data-wishlist-empty]");
    this.loadingState = this.querySelector("[data-wishlist-loading]");
    this.headerContainer = this.querySelector("[data-wishlist-header]");
    this.clearButton = this.querySelector("[data-clear-wishlist]");
    if (this.clearButton) {
      this.clearButton.addEventListener("click", () => {
        if (confirm("Are you sure you want to clear your entire wishlist?")) {
          this.clearWishlist();
        }
      });
    }
    this.renderWishlist();
    window.addEventListener("wishlist:updated", this.renderWishlist.bind(this));
  }
  disconnectedCallback() {
    window.removeEventListener(
      "wishlist:updated",
      this.renderWishlist.bind(this)
    );
  }
  async renderWishlist() {
    const wishlist = window.wishlistManager.getWishlist();
    if (wishlist.length === 0) {
      this.showEmptyState();
      return;
    }
    this.showLoadingState();
    try {
      const products = await this.fetchProducts(wishlist);
      this.renderProducts(products);
    } catch (error) {
      console.error("Error fetching wishlist products:", error);
      this.showError();
    }
  }
  async fetchProducts(productHandles) {
    const productPromises = productHandles.map(async (handle) => {
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (!response.ok) throw new Error(`Product ${handle} not found`);
        return await response.json();
      } catch (error) {
        console.error(`Failed to fetch product ${handle}:`, error);
        return null;
      }
    });
    const products = await Promise.all(productPromises);
    return products.filter((product) => product !== null);
  }
  renderProducts(products) {
    if (!this.productsContainer) return;
    this.productsContainer.innerHTML = products.map((product) => this.renderProductCard(product)).join("");
    this.showProductsState();
    this.attachRemoveListeners();
  }
  renderProductCard(product) {
    const price = this.formatMoney(product.price);
    const comparePrice = product.compare_at_price > product.price ? this.formatMoney(product.compare_at_price) : null;
    return `
      <div class="product-card group flex h-full flex-col" data-product-handle="${product.handle}">
        <div class="bg-white featured-image-wrapper relative aspect-square overflow-hidden">
          <a href="${product.url}" class="relative block h-full w-full">
            <img
              src="${product.featured_image}?width=600"
              alt="${product.title}"
              loading="lazy"
              class="relative z-10 h-full w-full object-contain transition duration-300"
            >
          </a>

          <button
            type="button"
            class="wishlist-remove-btn cursor-pointer absolute right-2 top-2 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-md transition-opacity hover:bg-gray-100"
            data-remove-product-handle="${product.handle}"
            aria-label="Remove from wishlist"
          >
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div class="flex flex-1 flex-col bg-white p-2 md:p-4">
          <div class="flex items-start justify-between gap-2">
            <div class="flex-1">
              <a href="${product.url}">
                <div class="group-hover:text-gray-darker mb-2 text-base transition-colors lg:text-lg">
                  ${product.title}
                </div>
              </a>

              <div class="product-card__price font-semibold">
                <span class="mr-2 text-base">${price}</span>
                ${comparePrice ? `<span class="text-gray text-sm line-through">${comparePrice}</span>` : ""}
              </div>
            </div>

            <a
              href="${product.url}"
              class="flex h-7 w-7 flex-shrink-0 items-center justify-center self-end rounded-full transition-opacity hover:opacity-80 md:h-9 md:w-9"
              aria-label="View product"
            >
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    `;
  }
  attachRemoveListeners() {
    const removeButtons = this.querySelectorAll("[data-remove-product-handle]");
    removeButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        const productHandle = button.getAttribute("data-remove-product-handle");
        window.wishlistManager.removeFromWishlist(productHandle);
      });
    });
  }
  formatMoney(cents) {
    if (window.money_format) {
      const amount = (cents / 100).toFixed(2);
      return window.money_format.replace(/\{\{\s*amount\s*\}\}/g, amount).replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.floor(cents / 100)).replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace(".", ","));
    }
    const currency = window.Shopify?.currency?.active || "EUR";
    return new Intl.NumberFormat(window.Shopify?.locale || "en", {
      style: "currency",
      currency
    }).format(cents / 100);
  }
  clearWishlist() {
    localStorage.removeItem("shopify_wishlist");
    window.wishlistManager.dispatchWishlistUpdate();
  }
  showLoadingState() {
    if (this.loadingState) this.loadingState.classList.remove("hidden");
    if (this.emptyState) this.emptyState.classList.add("hidden");
    if (this.productsContainer) this.productsContainer.classList.add("hidden");
    if (this.headerContainer) this.headerContainer.classList.add("hidden");
  }
  showEmptyState() {
    if (this.emptyState) this.emptyState.classList.remove("hidden");
    if (this.loadingState) this.loadingState.classList.add("hidden");
    if (this.productsContainer) this.productsContainer.classList.add("hidden");
    if (this.headerContainer) this.headerContainer.classList.add("hidden");
  }
  showProductsState() {
    if (this.productsContainer)
      this.productsContainer.classList.remove("hidden");
    if (this.loadingState) this.loadingState.classList.add("hidden");
    if (this.emptyState) this.emptyState.classList.add("hidden");
    if (this.headerContainer) this.headerContainer.classList.remove("hidden");
  }
  showError() {
    if (this.productsContainer) {
      this.productsContainer.innerHTML = `
        <div class="col-span-full py-12 text-center text-gray-500">
          <p>Error loading wishlist. Please try again.</p>
        </div>
      `;
    }
    this.showProductsState();
  }
}
window.customElements.define("wishlist-render", WishlistRender);
export {
  WishlistRender as default
};
